/**
 * Wishlist hook — persistent in DB with shareable links.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsAuth, pmsFrom } from '@/integrations/pms';
import type { WishlistItem } from '@/integrations/woo/types';

export const useWishlist = () => {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [wishlistId, setWishlistId] = useState<string | null>(null);

  useEffect(() => {
    pmsAuth.getSession().then((session) => {
      setUserId(session?.user?.id ?? null);
    });
    const unsubscribe = pmsAuth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => unsubscribe();
  }, []);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['wishlist', userId],
    queryFn: async (): Promise<WishlistItem[]> => {
      if (!userId) return [];

      let { data: wl } = await pmsFrom('wishlists')
        .select('id')
        .eq('user_id', userId)
        .eq('name', 'My Wishlist')
        .maybeSingle();

      if (!wl) {
        const { data: created } = await pmsFrom('wishlists')
          .insert({ user_id: userId, name: 'My Wishlist' })
          .select('id')
          .single();
        wl = created;
      }

      if (wl) {
        setWishlistId(wl.id);
        const { data: wishlistItems } = await pmsFrom('wishlist_items')
          .select('*')
          .eq('wishlist_id', wl.id)
          .order('added_at');
        return (wishlistItems || []) as WishlistItem[];
      }
      return [];
    },
    enabled: !!userId,
  });

  const addItem = useMutation({
    mutationFn: async (item: Omit<WishlistItem, 'id'>) => {
      if (!wishlistId) throw new Error('No wishlist');

      const { data: existing } = await pmsFrom('wishlist_items')
        .select('id')
        .eq('wishlist_id', wishlistId)
        .eq('product_id', item.product_id)
        .maybeSingle();

      if (existing) return;

      await pmsFrom('wishlist_items')
        .insert({ wishlist_id: wishlistId, ...item });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wishlist'] }),
  });

  const removeItem = useMutation({
    mutationFn: async (productId: string) => {
      if (!wishlistId) return;
      await pmsFrom('wishlist_items')
        .delete()
        .eq('wishlist_id', wishlistId)
        .eq('product_id', productId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wishlist'] }),
  });

  const isInWishlist = useCallback(
    (productId: string) => items.some(i => i.product_id === productId),
    [items]
  );

  const toggleItem = useCallback(
    (item: Omit<WishlistItem, 'id'>) => {
      if (isInWishlist(item.product_id)) {
        removeItem.mutate(item.product_id);
      } else {
        addItem.mutate(item);
      }
    },
    [isInWishlist, addItem, removeItem]
  );

  const getShareLink = useCallback(async (): Promise<string | null> => {
    if (!wishlistId) return null;

    await pmsFrom('wishlists')
      .update({ is_public: true })
      .eq('id', wishlistId);

    const { data } = await pmsFrom('wishlists')
      .select('share_token')
      .eq('id', wishlistId)
      .single();

    if (data?.share_token) {
      return `${window.location.origin}/shop/wishlist/${data.share_token}`;
    }
    return null;
  }, [wishlistId]);

  const moveToCart = useCallback(
    async (productId: string, addToCart: (item: any) => void) => {
      const item = items.find(i => i.product_id === productId);
      if (!item) return;

      addToCart({
        product_id: item.product_id,
        product_name: item.product_name,
        product_image: item.product_image,
        product_price: item.product_price,
        product_url: item.product_url,
        quantity: item.quantity,
        variation_id: null,
        variation_attributes: null,
        is_subscription: false,
        subscription_period: null,
        subscription_interval: null,
      });

      removeItem.mutate(productId);
    },
    [items, removeItem]
  );

  return {
    items,
    isLoading,
    isLoggedIn: !!userId,
    addItem: addItem.mutate,
    removeItem: removeItem.mutate,
    toggleItem,
    isInWishlist,
    getShareLink,
    moveToCart,
    itemCount: items.length,
  };
};
